import { useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { useNavigate } from 'react-router-dom';
import type { AuthCredentials } from '@/models/auth/types';
import { validateCredentials } from '@/models/auth/validation';
import { authenticateAD } from '@/models/auth/service';
import { setToken, setLogin } from '@/lib/tokenStorage';

export function useAuthController() {
  const navigate = useNavigate();

  const store = useLocalObservable(() => ({
    login: '',
    password: '',
    rememberMe: false,
    showPassword: false,
    isTyping: false,
    isLoading: false,
    error: null as string | null,
    isAuthenticated: false,
  }));

  function setField<K extends keyof AuthCredentials>(field: K, value: AuthCredentials[K]) {
    runInAction(() => {
      switch (field) {
        case 'login':
          store.login = value as string;
          break;
        case 'password':
          store.password = value as string;
          break;
        case 'rememberMe':
          store.rememberMe = value as boolean;
          break;
      }
      store.error = null;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const error = validateCredentials(store.login, store.password);
    if (error) {
      runInAction(() => {
        store.error = error;
      });
      return;
    }
    runInAction(() => {
      store.isLoading = true;
      store.error = null;
    });
    try {
      const token = await authenticateAD({
        login: store.login,
        password: store.password,
        rememberMe: store.rememberMe,
      });
      setToken(token, store.rememberMe);
      setLogin(store.login, store.rememberMe);
      runInAction(() => {
        store.isLoading = false;
        store.isAuthenticated = true;
      });
      navigate('/clients');
    } catch (err) {
      runInAction(() => {
        store.isLoading = false;
        store.error = err instanceof Error ? err.message : 'Ошибка авторизации.';
      });
    }
  }

  return { store, setField, handleSubmit };
}
